import { type FormEvent, useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'

const CODE_LENGTH = 6

/** Second step of signing in: the emailed code, sent as soon as all six digits are in. */
export function SignInCodeStep({
  isVerifying,
  isResending,
  errorMessage,
  notice,
  onSubmit,
  onResend,
  onChangeEmail,
}: {
  isVerifying: boolean
  isResending: boolean
  errorMessage?: string
  notice?: string
  onSubmit: (code: string) => void
  onResend: () => void
  onChangeEmail: () => void
}) {
  const [code, setCode] = useState('')

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (code.length === CODE_LENGTH && !isVerifying) onSubmit(code)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4">
      {errorMessage && (
        <Banner variant="error" small className="w-full">
          {errorMessage}
        </Banner>
      )}
      {notice && !errorMessage && (
        <p role="status" className="text-muted-foreground text-sm">
          {notice}
        </p>
      )}
      <InputOTP
        maxLength={CODE_LENGTH}
        value={code}
        onChange={setCode}
        onComplete={onSubmit}
        autoComplete="one-time-code"
        aria-label="Sign-in code"
        autoFocus
        disabled={isVerifying}
      >
        <InputOTPGroup>
          {Array.from({ length: CODE_LENGTH }).map((_, index) => (
            // The card shares the slots' surface color; the email field's border keeps them visible on it.
            <InputOTPSlot key={index} index={index} className="border" />
          ))}
        </InputOTPGroup>
      </InputOTP>
      <Button
        type="submit"
        className="w-full"
        disabled={code.length < CODE_LENGTH}
        loading={isVerifying ? 'Verifying…' : undefined}
      >
        Verify
      </Button>
      <div className="flex flex-wrap justify-center gap-x-4">
        <Button
          type="button"
          variant="link"
          size="sm"
          disabled={isVerifying}
          loading={isResending ? 'Sending…' : undefined}
          onClick={() => {
            setCode('')
            onResend()
          }}
        >
          Resend code
        </Button>
        <Button type="button" variant="link" size="sm" disabled={isVerifying} onClick={onChangeEmail}>
          Use a different email
        </Button>
      </div>
    </form>
  )
}
